import { useEffect, useState } from "react";
import { getInlineLoader } from "../Helpers";
import { FaLeaf, FaCheckCircle, FaShippingFast, FaArrowRight, FaGlobeAmericas, FaCertificate, FaHandshake } from "react-icons/fa";
import { GiMeat, GiCheckMark } from "react-icons/gi";
import { FaShield } from "react-icons/fa6";

const FEATURES = [
  {
    id: "mutton",
    icon: <GiMeat />,
    title: "Premium Mutton",
    description: "Hygienic processing of fresh and frozen mutton with custom cuts, tailored for Middle Eastern markets.",
    color: "#025025",
    tag: "High Protein"
  },
  {
    id: "avocados",
    icon: <FaLeaf />,
    title: "Fresh Avocados",
    description: "Export-grade Hass varieties sourced from sustainable Tanzanian farms and graded for global quality.",
    color: "#2d5a27",
    tag: "Grade A"
  },
  {
    id: "certifications",
    icon: <FaCheckCircle />,
    title: "Quality Compliance",
    description: "Fully compliant with Tanzania Meat Board and international safety standards from farm to table.",
    color: "#1f3c5a",
    tag: "Certified"
  },
  {
    id: "logistics",
    icon: <FaShippingFast />,
    title: "Supply Chain",
    description: "Efficient cold-chain logistics ensuring our agricultural products reach international ports in peak condition.",
    color: "#0e4c47",
    tag: "Global Reach"
  },
];

const CERT_LIST = [
  "Halal Certification", "Tanzania Meat Board", "Tax Clearance", 
  "Business License", "TIN & Incorporation"
];

export default function HomeBanner3() {
  const [isReady, setIsReady] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) return <div className="p-5 text-center">{getInlineLoader()}</div>;

  return (
    <section className="FeatureStageSection py-5" style={{ background: "#ffffff" }}>
      <div className="container">

        {/* --- NEW SECTION: COMPLIANCE & MARKETS --- */}
        <div className="row g-4 mb-5 pb-5 border-bottom">
          {/* Quality & Certifications */}
          <div className="col-lg-4">
            <div className="p-4 rounded-4 h-100" style={{ background: "#f1f5f9" }}>
              <div className="d-flex align-items-center gap-2 mb-3 text-success">
                <FaShield size={32} />
                <h5 className="fw-bold mb-0 text-dark">Quality & Compliance</h5>
              </div>
              <p className="small text-muted mb-4">Fully registered and adhering to international regulatory standards.</p>
              <ul className="list-unstyled">
                {CERT_LIST.map((cert, i) => (
                  <li key={i} className="d-flex align-items-center gap-2 mb-2 small fw-medium">
                    <GiCheckMark className="text-success" /> {cert}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Markets */}
          <div className="col-lg-4">
            <div className="p-4 rounded-4 h-100 border border-2">
              <div className="d-flex align-items-center gap-2 mb-3 text-primary">
                <FaGlobeAmericas size={28} />
                <h5 className="fw-bold mb-0 text-dark">Global Markets</h5>
              </div>
              <p className="text-muted small">
                Our primary export routes target the <strong>Middle East</strong> and strategic international destinations, delivering specifically to client demand.
              </p>
              <div className="mt-4 p-3 bg-light rounded-3 text-center">
                <span className="fw-bold text-navy h4">100% Export Ready</span>
              </div>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="col-lg-4">
            <div className="p-4 rounded-4 h-100 bg-dark text-white">
              <div className="d-flex align-items-center gap-2 mb-3">
                <FaHandshake size={28} className="text-info" />
                <h5 className="fw-bold mb-0">Why Choose Us</h5>
              </div>
              <div className="small opacity-75">
                <div className="mb-2">● High-quality products (International Standards)</div>
                <div className="mb-2">● Reliable cold-chain & timely delivery</div>
                <div className="mb-2">● Strong focus on customer satisfaction</div>
                <div>● Competitive global pricing</div>
              </div>
            </div>
          </div>
        </div>

        {/* --- EXISTING SECTION: INTERACTIVE FEATURE STAGE --- */}
        <header className="mb-5" data-aos="fade-up">
          <h2 className="display-5 font-bold text-navy">Refining the Export Supply Chain</h2>
          <p className="text-muted">A deep dive into our core operations and premium produce.</p>
        </header>

        <div className="row g-5 align-items-center">
          {/* Interactive Nav List */}
          <div className="col-lg-5" data-aos="fade-right">
            <div className="d-flex flex-column gap-3">
              {FEATURES.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(index)}
                  onMouseEnter={() => setActiveTab(index)}
                  className={`feature-nav-item ${activeTab === index ? 'active' : ''}`}
                  style={{ '--accent': item.color }}
                >
                  <div className="nav-icon">{item.icon}</div>
                  <div className="text-start">
                    <h4 className="fw-bold mb-0">{item.title}</h4>
                    <span className="small opacity-50">{item.tag}</span>
                  </div>
                  <FaArrowRight className="ms-auto arrow-icon" />
                </button>
              ))}
            </div>
          </div>

          {/* Visual Display Stage */}
          <div className="col-lg-7" data-aos="fade-left">
            <div className="stage-card shadow-lg rounded-5 overflow-hidden border-0">
              <div 
                className="stage-visual" 
                style={{ background: FEATURES[activeTab].color }}
              >
                <div className="stage-blob"></div>
                <div className="stage-icon-large">
                  {FEATURES[activeTab].icon}
                </div>
              </div>
              <div className="stage-body p-5 bg-white">
                <h3 className="h2 font-bold mb-3" style={{ color: FEATURES[activeTab].color }}>
                  {FEATURES[activeTab].title}
                </h3>
                <p className="text-muted fs-5 mb-0 leading-relaxed">
                  {FEATURES[activeTab].description}
                </p>
                <hr className="my-4 opacity-10" />
                <div className="d-flex align-items-center gap-3">
                   <div className="badge bg-light text-dark p-2 px-3 border">ISO Compliant</div>
                   <div className="badge bg-light text-dark p-2 px-3 border">Tanzanian Excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .text-navy { color: #0f172a; }
        
        .feature-nav-item {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px 25px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          transition: all 0.3s ease;
          color: #64748b;
          border-left: 5px solid transparent;
        }

        .feature-nav-item.active {
          background: #ffffff;
          border-color: #e2e8f0;
          border-left-color: var(--accent);
          color: #0f172a;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
          transform: translateX(10px);
        }

        .nav-icon { font-size: 1.5rem; transition: 0.3s; }
        .feature-nav-item.active .nav-icon { color: var(--accent); transform: scale(1.2); }
        .arrow-icon { opacity: 0; transition: 0.3s; }
        .feature-nav-item.active .arrow-icon { opacity: 1; color: var(--accent); }

        .stage-card { min-height: 500px; }
        .stage-visual {
          height: 220px;
          position: relative;
          display: grid;
          place-items: center;
          overflow: hidden;
          transition: background 0.5s ease;
        }

        .stage-icon-large {
          font-size: 6rem;
          color: rgba(255,255,255,0.2);
          z-index: 2;
          animation: float 6s ease-in-out infinite;
        }

        .stage-blob {
          position: absolute;
          width: 300px;
          height: 300px;
          background: rgba(255,255,255,0.1);
          border-radius: 50%;
          top: -100px;
          right: -100px;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        @media (max-width: 991px) {
          .feature-nav-item.active { transform: none; }
          .stage-card { min-height: auto; }
        }
      `}</style>
    </section>
  );
}